import { motion } from "framer-motion";
import { TESTIMONIALS } from "@/constants/testIds";
import { Quote, Check, CheckCheck } from "lucide-react";

const WA_CHAT = [
    { from: "them", text: "Bro your reels are hitting different this week 👀" },
    { from: "them", text: "We closed 2 clients from a single post. Two." },
    { from: "us", text: "That's the CASH loop. Reach → offer → sale." },
    { from: "them", text: "Never running ads again. Weekly retainer confirmed." },
    { from: "us", text: "Locked in. Next week we scale the winner." },
];

export default function Testimonials({ items = [] }) {
    // Duplicate for seamless marquee
    const track = [...items, ...items];

    return (
        <section
            id="testimonials"
            data-testid={TESTIMONIALS.root}
            className="relative py-24 md:py-32"
        >
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="max-w-3xl">
                    <p className="text-xs uppercase tracking-[0.35em] text-[var(--accent-gold)] mb-5">
                        Voices of the Vault
                    </p>
                    <h2 className="font-display font-bold text-4xl md:text-5xl lg:text-6xl leading-[1.05] text-[var(--text-primary)]">
                        Clients who never{" "}
                        <span className="italic text-[var(--accent-champagne)]">
                            ran an ad again.
                        </span>
                    </h2>
                </div>
            </div>

            {/* Written testimonials marquee */}
            <div
                data-testid={TESTIMONIALS.written}
                className="mt-14 md:mt-20 relative overflow-hidden"
            >
                <div className="absolute left-0 top-0 bottom-0 w-16 md:w-32 z-10 bg-gradient-to-r from-[#2a0303] to-transparent pointer-events-none" />
                <div className="absolute right-0 top-0 bottom-0 w-16 md:w-32 z-10 bg-gradient-to-l from-[#2a0303] to-transparent pointer-events-none" />

                <div className="flex gap-5 md:gap-6 marquee-track">
                    {track.map((t, i) => (
                        <article
                            key={`${t.id}-${i}`}
                            data-testid={TESTIMONIALS.card(t.id)}
                            className="shrink-0 w-[320px] md:w-[400px] rounded-2xl border border-[var(--border-subtle)] bg-[rgba(255,255,255,0.03)] p-6 md:p-7 relative"
                        >
                            <Quote
                                size={28}
                                className="text-[var(--accent-gold)] opacity-40 absolute top-5 right-5"
                            />
                            <p className="text-[var(--text-primary)] leading-relaxed text-[15px] md:text-base pr-6">
                                &ldquo;{t.quote}&rdquo;
                            </p>
                            <div className="mt-6 pt-5 border-t border-[var(--border-subtle)] flex items-center gap-3">
                                <div className="w-10 h-10 rounded-full bg-[var(--accent-gold)] text-[var(--brand-dark)] flex items-center justify-center font-bold text-sm">
                                    {t.avatar_initials || t.name.slice(0, 2).toUpperCase()}
                                </div>
                                <div>
                                    <p className="font-semibold text-[var(--text-primary)] text-sm">
                                        {t.name}
                                    </p>
                                    <p className="text-xs text-[var(--text-secondary)] uppercase tracking-[0.15em]">
                                        {t.business} · {t.industry}
                                    </p>
                                </div>
                            </div>
                        </article>
                    ))}
                </div>
            </div>

            {/* WhatsApp chat mockup */}
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-20 md:mt-28">
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-center">
                    <motion.div
                        initial={{ opacity: 0, x: -30 }}
                        whileInView={{ opacity: 1, x: 0 }}
                        viewport={{ once: true, margin: "-80px" }}
                        transition={{ duration: 0.8 }}
                        className="lg:col-span-7 order-2 lg:order-1"
                    >
                        <p className="text-xs uppercase tracking-[0.35em] text-[var(--accent-gold)] mb-4">
                            Straight from the DMs
                        </p>
                        <h3 className="font-display font-bold text-3xl md:text-4xl lg:text-5xl text-[var(--text-primary)] leading-tight">
                            Where every serious brand{" "}
                            <span className="italic text-[var(--accent-champagne)]">
                                actually talks
                            </span>{" "}
                            to us.
                        </h3>
                        <p className="mt-6 text-lg text-[var(--text-secondary)] max-w-lg">
                            Screenshots from client WhatsApp chats. Day-of-results, week-of-DMs,
                            month-of-money. Real quotes, real timestamps, ready to swap in.
                        </p>
                        <p className="mt-5 text-sm text-[var(--text-secondary)]/70 italic">
                            (Editable placeholder. Real client chat screenshots drop in here.)
                        </p>
                    </motion.div>

                    <motion.div
                        data-testid={TESTIMONIALS.whatsapp}
                        initial={{ opacity: 0, y: 40, rotateY: -8 }}
                        whileInView={{ opacity: 1, y: 0, rotateY: 0 }}
                        viewport={{ once: true, margin: "-80px" }}
                        transition={{ duration: 1 }}
                        className="lg:col-span-5 order-1 lg:order-2 flex justify-center perspective-1500"
                    >
                        <div
                            className="relative w-[280px] md:w-[320px] aspect-[9/19] rounded-[42px] bg-[#0a0000] border-[3px] border-[#111] shadow-[0_40px_80px_-20px_rgba(0,0,0,0.9),0_0_40px_-10px_rgba(212,175,55,0.2)]"
                            style={{
                                transform: "rotateY(-6deg) rotateX(3deg)",
                                transformStyle: "preserve-3d",
                            }}
                        >
                            <div className="absolute top-2.5 left-1/2 -translate-x-1/2 w-24 h-6 rounded-full bg-black" />
                            <div className="absolute inset-[10px] rounded-[36px] bg-[#0b141a] overflow-hidden flex flex-col">
                                <div className="bg-[#1f2c33] px-4 py-3 flex items-center gap-3 border-b border-black/40">
                                    <div className="w-8 h-8 rounded-full bg-[var(--accent-gold)] flex items-center justify-center text-[var(--brand-dark)] font-bold text-xs">
                                        AM
                                    </div>
                                    <div>
                                        <p className="text-white text-sm font-semibold">
                                            Client · Sunday
                                        </p>
                                        <p className="text-[10px] text-emerald-400">online</p>
                                    </div>
                                </div>
                                <div className="flex-1 px-3 py-4 space-y-2 overflow-y-auto text-[12px] leading-snug">
                                    {WA_CHAT.map((m, i) => (
                                        <div
                                            key={i}
                                            className={`max-w-[85%] px-3 py-2 rounded-lg ${
                                                m.from === "us"
                                                    ? "ml-auto bg-[#005c4b] text-white"
                                                    : "bg-[#202c33] text-white/90"
                                            }`}
                                        >
                                            {m.text}
                                            <div className="flex items-center justify-end gap-1 mt-1">
                                                <span className="text-[9px] text-white/50">
                                                    {String(9 + i).padStart(2, "0")}:
                                                    {String(12 + i * 7).padStart(2, "0")}
                                                </span>
                                                {m.from === "us" &&
                                                    (i < 2 ? (
                                                        <Check size={10} className="text-white/60" />
                                                    ) : (
                                                        <CheckCheck size={10} className="text-[#53bdeb]" />
                                                    ))}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                                <div className="bg-[#1f2c33] px-3 py-2 flex items-center gap-2">
                                    <div className="flex-1 h-8 rounded-full bg-[#2a3942] px-3 flex items-center text-[11px] text-white/50">
                                        Type a message
                                    </div>
                                </div>
                            </div>
                        </div>
                    </motion.div>
                </div>
            </div>
        </section>
    );
}
